import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';
import { TravelerRefSchema } from '../../shared/schema/traveler_ref.js';

/** Uma rodoviária como a busca de ônibus a conhece: o código do bus-api e o nome. */
const ChatUiBusPlannerStopSchema = z
  .object({ code: z.string().min(1), name: z.string().min(1) })
  .strict();

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** O dia existe no calendário? Conferido aqui, e não com o `parseDateOnly` do
 *  intelligence-ui-kit: a raiz do genui-kit roda nos backends (modal-session,
 *  conversation, analytics), cujas imagens não levam o intelligence-ui-kit — o
 *  import derrubava o boot com ERR_MODULE_NOT_FOUND. */
function isCalendarDay(day: string): boolean {
  const m = DAY_PATTERN.exec(day);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const date = Number(m[3]);
  const utc = new Date(Date.UTC(year, month, date));
  return utc.getUTCFullYear() === year && utc.getUTCMonth() === month && utc.getUTCDate() === date;
}

/** Um dia `YYYY-MM-DD` que existe no calendário (`2026-02-30` não passa): a
 *  busca que o agente lê nunca leva um dia impossível. O card de partidas usa
 *  o mesmo dia na faixa de datas. */
export const BusDaySchema = z.string().regex(DAY_PATTERN).refine(isCalendarDay);
const DAY = BusDaySchema;

const TERM = z.string().trim().min(1).max(80);

/** Quem viaja (#5472): 1..9, o teto da busca da bancada. */
const TravelersSchema = z.array(TravelerRefSchema).min(1).max(9);

/**
 * A busca de ônibus NA CONVERSA (`bus_planner`, frames 01–02 do Figma
 * "Conversacional → Bus"): a barra da bancada (Quem, Origem, Destino, Quando) num
 * card, para quando o agente ainda não tem o que buscar. Vem preenchida com o que
 * o agente já sabe; o Buscar devolve a busca por `interaction` (este challenge + a
 * `BusPlannerSearch`) — as rodoviárias viajam como código, nunca texto livre.
 * Sem `prefill.travelers` (blocos antigos) o card não mostra o Quem: quem viaja
 * é quem conversa.
 */
export const BusPlannerBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('bus_planner'),
    challengeId: z.string().min(1),
    /** Id de quem conversa — a linha "Você" do Quem. */
    requesterId: z.string().min(1).optional(),
    prefill: z
      .object({
        from: ChatUiBusPlannerStopSchema.optional(),
        to: ChatUiBusPlannerStopSchema.optional(),
        // O que a pessoa disse de uma parada que o agente ainda não resolveu
        // ("Brusque"): o campo vazio mostra o termo e abre buscando por ele. É só
        // o ponto de partida da busca — a rodoviária continua sendo escolhida na
        // lista, e o Buscar devolve código, nunca este texto.
        fromTerm: TERM.optional(),
        toTerm: TERM.optional(),
        outwardDate: DAY.optional(),
        returnDate: DAY.optional(),
        /** O Quem semeado: quem conversa, ou quem a busca anterior levou. */
        travelers: TravelersSchema.optional(),
        policyTravelerId: z.string().min(1).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type BusPlannerBlock = z.infer<typeof BusPlannerBlockSchema>;

/** A busca do card, ecoada em `confirmation.selections`: as duas rodoviárias,
 *  os dias (`returnDate` ausente ⇒ só ida) e, com o Quem, quem viaja e de quem é
 *  a política. Sem `travelers`, viaja quem conversa. Os ids são eco do cliente:
 *  o agente os confere no cliente da conversa antes de usá-los. */
export const BusPlannerSearchSchema = z
  .object({
    from: ChatUiBusPlannerStopSchema,
    to: ChatUiBusPlannerStopSchema,
    outwardDate: DAY,
    returnDate: DAY.optional(),
    travelers: TravelersSchema.optional(),
    policyTravelerId: z.string().min(1).optional(),
  })
  .strict();
export type BusPlannerSearch = z.infer<typeof BusPlannerSearchSchema>;
